"use client";

import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import ar from "@/i18n/ar.json";
import en from "@/i18n/en.json";

// ─── Types ───────────────────────────────────────────────
export type Locale = "ar" | "en";
export type Direction = "rtl" | "ltr";
export type Translations = typeof ar;

export interface LanguageContextValue {
  locale: Locale;
  dir: Direction;
  t: Translations;
  toggleLocale: () => void;
  setLocale: (locale: Locale) => void;
  isRTL: boolean;
}

// ─── Translations Map ────────────────────────────────────
const translations: Record<Locale, Translations> = { ar, en };

// ─── Context ─────────────────────────────────────────────
export const LanguageContext = createContext<LanguageContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────
interface LanguageProviderProps {
  children: ReactNode;
  defaultLocale?: Locale;
}

export function LanguageProvider({
  children,
  defaultLocale = "ar",
}: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);

  const toggleLocale = useCallback(() => {
    setLocaleState((prev) => (prev === "ar" ? "en" : "ar"));
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
  }, []);

  const value = useMemo<LanguageContextValue>(() => {
    const currentTranslations = translations[locale];
    return {
      locale,
      dir: (locale === "ar" ? "rtl" : "ltr") as Direction,
      t: currentTranslations,
      toggleLocale,
      setLocale,
      isRTL: locale === "ar",
    };
  }, [locale, toggleLocale, setLocale]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
